import type {
  CharacterPortrait,
  GameState,
  InventoryEntry,
  InventoryPersonStatus,
  RecordCardScene,
  ScenarioInventory,
  Scene,
} from '../types';
import { resolveCharacterPortrait } from './character-runtime';
import { ConditionEvaluator } from './condition-evaluator';
import { formatRecordCardSerialNumber } from './record-card-content';

export interface InventoryCampaignStore {
  unlocked: string[];
  opened: string[];
}

const CAMPAIGN_KEY_PREFIX = 'vn_inventory_campaign_';

export const INVENTORY_SECTION_ORDER = ['adversaires', 'attaques', 'documents'] as const;

export type InventorySectionId = (typeof INVENTORY_SECTION_ORDER)[number];

export type InventoryUnlockKind = InventorySectionId;

export interface InventoryUnlockNotice {
  id: string;
  name: string;
  kind: InventoryUnlockKind;
  /** Scenario-relative asset path, if any. */
  image?: string;
}

export interface InventoryTileView {
  id: string;
  name: string;
  unlocked: boolean;
  opened: boolean;
  image?: string;
  serialLabel?: string | null;
  dangerScore?: number;
  dangerMax?: number;
  dangerWord?: string;
  isDirectory?: boolean;
  directoryNameCount?: number;
}

export interface InventorySectionView {
  id: string;
  title: string;
  unlockedCount: number;
  totalCount: number;
  layout: 'tiles' | 'pills';
  tiles: InventoryTileView[];
}

export interface InventoryDirectoryPersonView {
  id: string;
  name: string;
  status: InventoryPersonStatus;
  previous?: string;
  note?: string;
}

export interface InventoryDirectoryGroupView {
  title: string;
  unlockedCount: number;
  totalCount: number;
  entries: InventoryDirectoryPersonView[];
}

function emptyStore(): InventoryCampaignStore {
  return { unlocked: [], opened: [] };
}

export function loadInventoryCampaignStore(scenarioId: string): InventoryCampaignStore {
  try {
    const raw = localStorage.getItem(`${CAMPAIGN_KEY_PREFIX}${scenarioId}`);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as InventoryCampaignStore;
    return {
      unlocked: Array.isArray(parsed.unlocked) ? [...parsed.unlocked] : [],
      opened: Array.isArray(parsed.opened) ? [...parsed.opened] : [],
    };
  } catch {
    return emptyStore();
  }
}

export function saveInventoryCampaignStore(scenarioId: string, store: InventoryCampaignStore): void {
  localStorage.setItem(`${CAMPAIGN_KEY_PREFIX}${scenarioId}`, JSON.stringify(store));
}

function isEntryConditionMet(
  condition: string | undefined,
  gameState: GameState,
): boolean {
  if (!condition || condition.trim().length === 0) return true;
  try {
    return ConditionEvaluator.evaluate(condition, gameState);
  } catch {
    return false;
  }
}

function collectDirectoryPersonIds(entry: InventoryEntry): string[] {
  if (entry.form !== 'directory' || !entry.groups) return [];
  const ids: string[] = [];
  for (const group of entry.groups) {
    for (const person of group.entries ?? []) {
      ids.push(person.id);
    }
  }
  return ids;
}

function isDirectoryEntryUnlocked(entry: InventoryEntry, gameState: GameState): boolean {
  if (entry.form !== 'directory' || !entry.groups) return false;
  for (const group of entry.groups) {
    for (const person of group.entries ?? []) {
      if (isEntryConditionMet(person.condition, gameState)) {
        return true;
      }
    }
  }
  return false;
}

function countDirectoryUnlockedNames(entry: InventoryEntry, gameState: GameState): number {
  if (entry.form !== 'directory' || !entry.groups) return 0;
  let count = 0;
  for (const group of entry.groups) {
    for (const person of group.entries ?? []) {
      if (isEntryConditionMet(person.condition, gameState)) {
        count += 1;
      }
    }
  }
  return count;
}

/**
 * Returns catalog ids whose condition is currently true (entries + directory persons).
 */
export function evaluateInventoryUnlocks(
  inventory: ScenarioInventory | undefined,
  gameState: GameState,
): string[] {
  if (!inventory?.entries?.length) return [];

  const unlocks: string[] = [];
  for (const entry of inventory.entries) {
    if (entry.form === 'directory') {
      for (const group of entry.groups ?? []) {
        for (const person of group.entries ?? []) {
          if (isEntryConditionMet(person.condition, gameState)) {
            unlocks.push(person.id);
          }
        }
      }
      if (isDirectoryEntryUnlocked(entry, gameState)) {
        unlocks.push(entry.id);
      }
    } else if (isEntryConditionMet(entry.condition, gameState)) {
      unlocks.push(entry.id);
    }
  }
  return unlocks;
}

function resolveUnlockKind(
  inventory: ScenarioInventory | undefined,
  id: string,
): InventoryUnlockKind | null {
  if (!inventory?.entries?.length) return null;

  for (const entry of inventory.entries) {
    if (entry.id === id) {
      return INVENTORY_SECTION_ORDER.includes(entry.section as InventorySectionId)
        ? (entry.section as InventoryUnlockKind)
        : null;
    }
    if (entry.form === 'directory') {
      for (const group of entry.groups ?? []) {
        if (group.entries?.some((person) => person.id === id)) {
          return INVENTORY_SECTION_ORDER.includes(entry.section as InventorySectionId)
            ? (entry.section as InventoryUnlockKind)
            : null;
        }
      }
    }
  }
  return null;
}

function findCatalogEntryForId(
  inventory: ScenarioInventory | undefined,
  id: string,
): { entry: InventoryEntry; personName?: string } | null {
  if (!inventory?.entries?.length) return null;

  for (const entry of inventory.entries) {
    if (entry.id === id) {
      return { entry };
    }
    if (entry.form === 'directory') {
      for (const group of entry.groups ?? []) {
        for (const person of group.entries ?? []) {
          if (person.id === id) {
            return { entry, personName: person.name };
          }
        }
      }
    }
  }
  return null;
}

/**
 * Builds a HUD unlock notice for a catalog id (entry or directory person).
 */
export function buildInventoryUnlockNotice(
  inventory: ScenarioInventory | undefined,
  id: string,
  options?: InventorySectionViewOptions,
): InventoryUnlockNotice | null {
  const kind = resolveUnlockKind(inventory, id);
  if (!kind) return null;

  const located = findCatalogEntryForId(inventory, id);
  if (!located) return null;

  const { entry, personName } = located;
  const linkedScene = findInventoryLinkedRecordCard(entry, options?.scenes);
  const image = personName
    ? undefined
    : resolveInventoryEntryImage(entry, linkedScene, options?.characters);

  return {
    id,
    name: personName ?? entry.name,
    kind,
    image,
  };
}

/**
 * New catalog unlocks since the previous store snapshot, mapped to HUD notices.
 * Directory parent ids are skipped when a person from that directory is in the same batch.
 */
export function diffInventoryUnlockNotices(
  inventory: ScenarioInventory | undefined,
  previousUnlocked: string[],
  nextUnlocked: string[],
  options?: InventorySectionViewOptions,
): InventoryUnlockNotice[] {
  const previous = new Set(previousUnlocked);
  const added = nextUnlocked.filter((id) => !previous.has(id));
  if (added.length === 0) return [];

  const addedSet = new Set(added);
  const skipParentIds = new Set<string>();
  for (const entry of inventory?.entries ?? []) {
    if (entry.form !== 'directory') continue;
    const personIds = collectDirectoryPersonIds(entry);
    if (personIds.some((personId) => addedSet.has(personId))) {
      skipParentIds.add(entry.id);
    }
  }

  const notices: InventoryUnlockNotice[] = [];
  for (const id of added) {
    if (skipParentIds.has(id)) continue;
    const notice = buildInventoryUnlockNotice(inventory, id, options);
    if (notice) notices.push(notice);
  }
  return notices;
}

/** @deprecated Use diffInventoryUnlockNotices for drawer payloads. */
export function diffInventoryUnlockPings(
  inventory: ScenarioInventory | undefined,
  previousUnlocked: string[],
  nextUnlocked: string[],
): InventoryUnlockKind[] {
  return diffInventoryUnlockNotices(inventory, previousUnlocked, nextUnlocked).map(
    (notice) => notice.kind,
  );
}

/**
 * Merges newly met conditions into the campaign store (union, never removes).
 */
export function syncInventoryCampaignStore(
  scenarioId: string,
  inventory: ScenarioInventory | undefined,
  gameState: GameState,
): InventoryCampaignStore {
  const store = loadInventoryCampaignStore(scenarioId);
  const currentlyMet = new Set(evaluateInventoryUnlocks(inventory, gameState));
  const unlockedSet = new Set(store.unlocked);

  let changed = false;
  for (const id of currentlyMet) {
    if (!unlockedSet.has(id)) {
      unlockedSet.add(id);
      changed = true;
    }
  }

  if (!changed) return store;

  const next: InventoryCampaignStore = {
    unlocked: [...unlockedSet],
    opened: store.opened,
  };
  saveInventoryCampaignStore(scenarioId, next);
  return next;
}

export function markInventoryEntryOpened(
  scenarioId: string,
  entryId: string,
): InventoryCampaignStore {
  const store = loadInventoryCampaignStore(scenarioId);
  if (store.opened.includes(entryId)) return store;
  const next: InventoryCampaignStore = {
    unlocked: store.unlocked,
    opened: [...store.opened, entryId],
  };
  saveInventoryCampaignStore(scenarioId, next);
  return next;
}

function isCatalogIdUnlocked(
  id: string,
  gameState: GameState,
  inventory: ScenarioInventory,
  store: InventoryCampaignStore,
): boolean {
  if (store.unlocked.includes(id)) return true;
  return evaluateInventoryUnlocks(inventory, gameState).includes(id);
}

function isEntryUnlockedForDisplay(
  entry: InventoryEntry,
  gameState: GameState,
  store: InventoryCampaignStore,
): boolean {
  if (entry.form === 'directory') {
    return isDirectoryEntryUnlocked(entry, gameState) || store.unlocked.includes(entry.id);
  }
  return isEntryConditionMet(entry.condition, gameState) || store.unlocked.includes(entry.id);
}

function recordCardById(
  scenes: Scene[] | undefined,
  sceneId: string | undefined,
): RecordCardScene | undefined {
  if (!sceneId || !scenes?.length) return undefined;
  const scene = scenes.find((item) => item.id === sceneId);
  if (!scene || scene.type !== 'recordCard') return undefined;
  return scene as RecordCardScene;
}

/**
 * Linked recordCard for a catalogue entry: `card` first, else a lone `visited:sceneId`
 * condition when that scene is itself a recordCard.
 */
export function findInventoryLinkedRecordCard(
  entry: InventoryEntry,
  scenes: Scene[] | undefined,
): RecordCardScene | undefined {
  const fromCard = recordCardById(scenes, entry.card);
  if (fromCard) return fromCard;
  const visitedMatch = entry.condition?.match(/^visited:([a-zA-Z_][a-zA-Z0-9_]*)$/);
  return recordCardById(scenes, visitedMatch?.[1]);
}

/**
 * Portrait / illustration for a catalogue entry.
 * `entry.image` wins; otherwise the linked recordCard `image`, then `characterId` → defaultImage.
 */
export function resolveInventoryEntryImage(
  entry: InventoryEntry,
  scene: RecordCardScene | undefined,
  characters?: CharacterPortrait[],
): string | undefined {
  if (entry.image) return entry.image;
  if (scene?.image) return scene.image;
  if (scene?.characterId) {
    return resolveCharacterPortrait(characters, scene.characterId)?.defaultImage;
  }
  return undefined;
}

export interface InventorySectionViewOptions {
  scenes?: Scene[];
  characters?: CharacterPortrait[];
}

export function buildInventorySectionViews(
  inventory: ScenarioInventory | undefined,
  gameState: GameState,
  store: InventoryCampaignStore,
  options?: InventorySectionViewOptions,
): InventorySectionView[] {
  if (!inventory?.sections?.length) return [];

  const sectionMeta = new Map(inventory.sections.map((s) => [s.id, s.title]));
  const entriesBySection = new Map<string, InventoryEntry[]>();

  for (const entry of inventory.entries ?? []) {
    const list = entriesBySection.get(entry.section) ?? [];
    list.push(entry);
    entriesBySection.set(entry.section, list);
  }

  return INVENTORY_SECTION_ORDER.filter((id) => sectionMeta.has(id)).map((sectionId) => {
    const entries = entriesBySection.get(sectionId) ?? [];
    const layout: 'tiles' | 'pills' = sectionId === 'adversaires' ? 'tiles' : 'pills';
    const tiles: InventoryTileView[] = entries.map((entry) => {
      const unlocked = isEntryUnlockedForDisplay(entry, gameState, store);
      const opened = store.opened.includes(entry.id);
      if (entry.form === 'directory') {
        return {
          id: entry.id,
          name: entry.name,
          unlocked,
          opened,
          isDirectory: true,
          directoryNameCount: countDirectoryUnlockedNames(entry, gameState),
        };
      }
      const linkedScene = findInventoryLinkedRecordCard(entry, options?.scenes);
      const danger = entry.danger ?? linkedScene?.danger;
      return {
        id: entry.id,
        name: entry.name,
        unlocked,
        opened,
        image: resolveInventoryEntryImage(entry, linkedScene, options?.characters),
        serialLabel: formatRecordCardSerialNumber(entry.serial),
        dangerScore: danger?.score,
        dangerMax: danger?.max ?? 10,
        dangerWord: danger?.word,
      };
    });

    const totalCount = entries.length;
    const unlockedCount = tiles.filter((t) => t.unlocked).length;

    return {
      id: sectionId,
      title: sectionMeta.get(sectionId) ?? sectionId,
      unlockedCount,
      totalCount,
      layout,
      tiles,
    };
  });
}

export function hasScenarioInventory(inventory: ScenarioInventory | undefined): boolean {
  return Boolean(inventory?.sections?.length && inventory.entries?.length);
}

/**
 * Builds visible directory groups for the detail panel — only unlocked persons,
 * and only groups that have at least one unlocked person.
 */
export function buildDirectoryGroupViews(
  entry: InventoryEntry,
  unlockedIds: string[],
): InventoryDirectoryGroupView[] {
  if (entry.form !== 'directory' || !entry.groups?.length) return [];

  const unlockedSet = new Set(unlockedIds);

  return entry.groups
    .map((group) => {
      const allEntries = group.entries ?? [];
      const visibleEntries = allEntries
        .filter((person) => unlockedSet.has(person.id))
        .map((person) => ({
          id: person.id,
          name: person.name,
          status: person.status,
          previous: person.previous,
          note: person.note,
        }));

      if (visibleEntries.length === 0) return null;

      return {
        title: group.title,
        unlockedCount: visibleEntries.length,
        totalCount: allEntries.length,
        entries: visibleEntries,
      };
    })
    .filter((group): group is InventoryDirectoryGroupView => group !== null);
}
